import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout';

const PrivacyPolicy: React.FC = () => {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="August 13, 2026">
      <p>
        This Privacy Policy explains how Where To Invest (“WTI”, “we”, “us”, or
        “our”) collects, uses, shares, and protects personal information when you
        use our website, application, and related services (the “Service”).
      </p>

      <h2>1. Information We Collect</h2>
      <h3>Account and identity information</h3>
      <ul>
        <li>Name, email address, phone number (if provided)</li>
        <li>Login credentials or password setup status</li>
        <li>Account role and status</li>
      </ul>

      <h3>Subscription and billing-related information</h3>
      <ul>
        <li>Plan / package details, usage counters, and access dates</li>
        <li>
          Payment and order references from our checkout or CRM partners (we do
          not store full payment card numbers in the WTI app)
        </li>
      </ul>

      <h3>Service usage information</h3>
      <ul>
        <li>Addresses and locations you evaluate</li>
        <li>Saved reports, evaluation history, and feature usage</li>
        <li>Support requests and related communications</li>
      </ul>

      <h3>Technical information</h3>
      <ul>
        <li>IP address, browser type, device information, and approximate logs</li>
        <li>Cookies or similar technologies needed for authentication and security</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>Create and manage your account</li>
        <li>Provide reports, training access, and subscription features</li>
        <li>Send magic-link login, password reset, and service emails</li>
        <li>Send renewal reminders and account notices</li>
        <li>Enforce plan limits and prevent abuse</li>
        <li>Improve product performance, reliability, and support</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>3. How We Share Information</h2>
      <p>We may share information with:</p>
      <ul>
        <li>
          <strong>Service providers</strong> that help us operate the Service
          (for example hosting, email delivery, analytics, customer support)
        </li>
        <li>
          <strong>Payment / CRM partners</strong> used for checkout,
          subscriptions, and purchase fulfillment
        </li>
        <li>
          <strong>Data and map providers</strong> needed to generate evaluations
          and location insights
        </li>
        <li>
          <strong>Legal or safety recipients</strong> when required by law or to
          protect rights, security, or users
        </li>
      </ul>
      <p>We do not sell your personal information for money.</p>

      <h2>4. Cookies and Similar Technologies</h2>
      <p>
        We use essential cookies or local storage to keep you signed in and to
        protect account security. Some third-party tools embedded in the Service
        may set their own cookies according to their policies.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We keep account, subscription, and evaluation data for as long as needed
        to provide the Service, meet legal or accounting requirements, resolve
        disputes, and enforce our agreements. When data is no longer needed, we
        delete or de-identify it where reasonably possible.
      </p>

      <h2>6. Security</h2>
      <p>
        We use administrative, technical, and organizational measures designed to
        protect personal information, including encrypted transport and access
        controls. No method of transmission or storage is completely secure.
      </p>

      <h2>7. Your Choices</h2>
      <ul>
        <li>Update profile details in Settings</li>
        <li>Change or set your password</li>
        <li>Cancel your subscription according to plan terms</li>
        <li>
          Request account or data help by contacting
          {' '}<a href="mailto:support@wheretoinvest.io">support@wheretoinvest.io</a>
        </li>
      </ul>

      <h2>8. Children’s Privacy</h2>
      <p>
        The Service is not directed to children under 18. We do not knowingly
        collect personal information from children.
      </p>

      <h2>9. International Users</h2>
      <p>
        If you access the Service from outside the country where our systems
        operate, your information may be processed in other locations with
        different data-protection laws.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy periodically. The “Last updated” date
        will reflect the latest revision. Continued use of the Service after
        changes means you acknowledge the updated Policy.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        Privacy questions: {' '}
        <a href="mailto:support@wheretoinvest.io">support@wheretoinvest.io</a>
        <br />
        Website:{' '}
        <a href="https://wheretoinvest.io" target="_blank" rel="noopener noreferrer">
          wheretoinvest.io
        </a>
      </p>
    </LegalPageLayout>
  );
};

export default PrivacyPolicy;
